import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, BookOpen, ChevronDown, ChevronRight, Search, GraduationCap, Trophy, Star } from 'lucide-react';
import api from '../../api';

/* ── Subject Leaderboard ──────────────────────────────────────────────── */
function SubjectLeaderboard({ subject, classId, className }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]     = useState(false);

  const load = useCallback(async () => {
    if (loading || data) return;
    setLoading(true);
    try {
      const r = await api.get(`/teachers/me/subject-leaderboard?subject_name=${encodeURIComponent(subject)}&class_id=${classId}&limit=5`);
      setData(r.data || []);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [subject, classId, loading, data]);

  const handleToggle = () => {
    setOpen(o => !o);
    if (!open && !data) load();
  };

  const medalColor = (i) => i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : i === 2 ? '#D97706' : '#CBD5E1';

  return (
    <div className="border-t border-slate-100 bg-gradient-to-b from-amber-50/40 to-transparent">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-2 px-5 py-3 hover:bg-amber-50/60 transition-colors"
        data-testid={`leaderboard-toggle-${classId}-${subject}`}
      >
        <Trophy className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" strokeWidth={2} />
        <span className="text-xs font-semibold text-amber-700">Top Performers — {className}</span>
        {open ? <ChevronDown className="w-3 h-3 text-amber-400 ml-auto" /> : <ChevronRight className="w-3 h-3 text-amber-400 ml-auto" />}
      </button>

      {open && (
        <div className="px-5 pb-4">
          {loading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-8 bg-amber-50 rounded-lg animate-pulse" />)}</div>
          ) : !data || data.length === 0 ? (
            <p className="text-xs text-slate-400 py-2 text-center">No exam scores yet for this subject</p>
          ) : (
            <div className="space-y-2">
              {data.map((s, i) => (
                <div key={s.student_id} className="flex items-center gap-3" data-testid={`leaderboard-row-${i}`}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${medalColor(i)}20` }}>
                    {i < 3 ? <Trophy className="w-2.5 h-2.5" style={{ color: medalColor(i) }} strokeWidth={2.5} />
                      : <span className="text-[10px] font-bold text-slate-400">{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{s.student_name}</p>
                    <p className="text-[10px] text-slate-400">{s.exam_count} exam{s.exam_count !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Star className="w-2.5 h-2.5 text-amber-400" strokeWidth={2.5} />
                    <span className="text-xs font-bold text-slate-700">{s.avg_score}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyStudentsPage() {
  const [myAssignments, setMyAssignments] = useState([]);
  const [studentsByClass, setStudentsByClass] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('by-class');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get('/academics/my-assignments');
        const assignments = r.data || [];
        setMyAssignments(assignments);

        // Get unique class IDs
        const seen = new Set();
        const classIds = [];
        for (const a of assignments) {
          if (a.class_id && !seen.has(a.class_id)) {
            seen.add(a.class_id);
            classIds.push({ id: a.class_id, name: a.class_name });
          }
        }
        if (r.class_teacher_for && !seen.has(r.class_teacher_for.id)) {
          classIds.unshift(r.class_teacher_for);
        }

        // Fetch students for all classes in parallel
        const results = await Promise.all(
          classIds.map(cls =>
            api.get(`/students?class_id=${cls.id}&status=active&limit=100`)
              .then(res => ({ classId: cls.id, className: cls.name, students: res.data || [] }))
              .catch(() => ({ classId: cls.id, className: cls.name, students: [] }))
          )
        );

        const map = {};
        for (const r of results) {
          map[r.classId] = { name: r.className, students: r.students };
        }
        setStudentsByClass(map);

        // Auto-expand first class
        if (classIds.length > 0) setExpanded({ [classIds[0].id]: true });
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Unique classes list
  const classes = useMemo(() => Object.entries(studentsByClass).map(([id, v]) => ({ id, name: v.name, count: v.students.length })), [studentsByClass]);

  // Total students (deduplicated by student id)
  const totalStudents = useMemo(() => {
    const ids = new Set();
    Object.values(studentsByClass).forEach(({ students }) => students.forEach(s => ids.add(s.id)));
    return ids.size;
  }, [studentsByClass]);

  // Subjects with their classes and student counts
  const subjectData = useMemo(() => {
    const map = {};
    for (const a of myAssignments) {
      const subj = a.subject_name;
      if (!subj) continue;
      if (!map[subj]) map[subj] = [];
      if (!map[subj].find(c => c.id === a.class_id)) {
        map[subj].push({ id: a.class_id, name: a.class_name, count: studentsByClass[a.class_id]?.students?.length || 0 });
      }
    }
    return Object.entries(map).map(([subj, classArr]) => ({
      subject: subj,
      classes: classArr,
      total: classArr.reduce((s, c) => s + c.count, 0),
    }));
  }, [myAssignments, studentsByClass]);

  const q = search.trim().toLowerCase();

  const filteredStudents = (students) =>
    q ? students.filter(s => `${s.first_name} ${s.last_name} ${s.admission_number || ''}`.toLowerCase().includes(q)) : students;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="my-students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">My Students</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {loading ? 'Loading...' : `${totalStudents} student${totalStudents !== 1 ? 's' : ''} across ${classes.length} class${classes.length !== 1 ? 'es' : ''}`}
          </p>
        </div>
        <div className="relative w-full sm:w-56">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search students..."
            className="w-full pl-8 pr-3 h-9 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#516EFA]/20"
            data-testid="student-search-input"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="students-tab-group">
        <button
          onClick={() => setTab('by-class')}
          data-testid="tab-by-class"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === 'by-class' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" />By Class
        </button>
        <button
          onClick={() => setTab('by-subject')}
          data-testid="tab-by-subject"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === 'by-subject' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          <BookOpen className="w-3.5 h-3.5 inline mr-1.5" />By Subject
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : classes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 py-20 text-center">
          <div className="w-12 h-12 rounded-xl bg-[#eef1ff] flex items-center justify-center mx-auto mb-3">
            <GraduationCap className="w-6 h-6 text-[#516EFA]" />
          </div>
          <p className="text-slate-800 font-semibold">No classes assigned yet</p>
          <p className="text-slate-500 text-sm mt-1">Ask your school admin to assign you to a class or subject</p>
        </div>
      ) : tab === 'by-class' ? (
        // ── BY CLASS ──────────────────────────────────────────────────
        <div className="space-y-3">
          {classes.map(cls => {
            const students = filteredStudents(studentsByClass[cls.id]?.students || []);
            const isOpen = !!expanded[cls.id];
            return (
              <div key={cls.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid={`class-section-${cls.id}`}>
                <button
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(p => ({ ...p, [cls.id]: !p[cls.id] }))}
                  data-testid={`class-toggle-${cls.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#eef1ff] flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-[#516EFA]" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-900 text-sm">{cls.name}</p>
                      <p className="text-xs text-slate-500">{cls.count} student{cls.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100">
                    {students.length === 0 ? (
                      <p className="text-center text-xs text-slate-400 py-6">
                        {q ? 'No students match your search' : 'No students in this class'}
                      </p>
                    ) : (
                      <div className="divide-y divide-slate-50">
                        {students.map((s, idx) => (
                          <div key={s.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`student-row-${s.id}`}>
                            <span className="text-xs text-slate-400 w-5 flex-shrink-0">{idx + 1}</span>
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-[10px] flex-shrink-0">
                              {`${s.first_name?.[0] || ''}${s.last_name?.[0] || ''}`.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{s.first_name} {s.last_name}</p>
                              <p className="text-xs text-slate-400">{s.admission_number || 'N/A'}</p>
                            </div>
                            <span className="text-xs text-slate-400 hidden sm:block">{s.gender || ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // ── BY SUBJECT ────────────────────────────────────────────────
        <div className="space-y-3">
          {subjectData.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 py-16 text-center">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No subjects assigned yet</p>
            </div>
          ) : subjectData.map(({ subject, classes: subjectClasses, total }) => {
            const isOpen = !!expanded[`subj-${subject}`];
            return (
              <div key={subject} className="bg-white rounded-xl border border-slate-100 overflow-hidden" data-testid={`subject-section-${subject}`}>
                <button
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(p => ({ ...p, [`subj-${subject}`]: !p[`subj-${subject}`] }))}
                  data-testid={`subject-toggle-${subject}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-900 text-sm">{subject}</p>
                      <p className="text-xs text-slate-500">
                        {subjectClasses.map(c => c.name).join(', ')} · {total} student{total !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100">
                    {subjectClasses.map(cls => {
                      const students = filteredStudents(studentsByClass[cls.id]?.students || []);
                      if (students.length === 0 && q) return null;
                      return (
                        <div key={cls.id}>
                          <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{cls.name} — {students.length} student{students.length !== 1 ? 's' : ''}</p>
                          </div>
                          {students.length === 0 ? (
                            <p className="text-center text-xs text-slate-400 py-4">{q ? 'No students match search' : 'No students'}</p>
                          ) : (
                            <div className="divide-y divide-slate-50">
                              {students.map((s, idx) => (
                                <div key={s.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                                  <span className="text-xs text-slate-400 w-5 flex-shrink-0">{idx + 1}</span>
                                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-[10px] flex-shrink-0">
                                    {`${s.first_name?.[0] || ''}${s.last_name?.[0] || ''}`.toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 truncate">{s.first_name} {s.last_name}</p>
                                    <p className="text-xs text-slate-400">{s.admission_number || 'N/A'}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Subject Leaderboard for this class */}
                          <SubjectLeaderboard subject={subject} classId={cls.id} className={cls.name} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
